import React from 'react';
import { Shield, Scale, BookOpen, ExternalLink, Info, CheckCircle2 } from 'lucide-react';
import { antisemitismFrameworks } from '../lib/data';
type Criterion = {
  id: string;
  label: string;
  text: string;
};
function CriterionRow({
  criterion,
  protectedItem = false
}: {
  criterion: Criterion;
  protectedItem?: boolean;
}) {
  return <li className={`flex items-start gap-3 px-4 py-3 ${protectedItem ? 'bg-emerald-50/60' : ''}`} data-id="element-903">
      <span className={`mt-0.5 inline-flex items-center justify-center text-[10px] uppercase tracking-wider font-heading px-2 py-0.5 rounded-md flex-shrink-0 ${protectedItem ? 'bg-emerald-100 text-emerald-700' : 'bg-muted text-muted-foreground'}`} style={{
      fontVariantNumeric: 'tabular-nums'
    }} data-id="element-904">
        {criterion.label}
      </span>
      <span className="text-sm leading-relaxed text-foreground" data-id="element-905">
        {criterion.text}
      </span>
    </li>;
}
function FrameworkCard({
  icon: Icon,
  title,
  year,
  source,
  definition,
  note,
  children
}: {
  icon: React.ElementType;
  title: string;
  year: number;
  source: string;
  definition: string;
  note?: string;
  children: React.ReactNode;
}) {
  return <section className="bg-card border border-border rounded-lg overflow-hidden" data-id="element-906">
      <header className="px-6 pt-6 pb-4 border-b border-border" data-id="element-907">
        <div className="flex items-start gap-4" data-id="element-908">
          <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0" data-id="element-909">
            <Icon className="h-5 w-5 text-accent" data-id="element-910" />
          </div>
          <div className="min-w-0 flex-1" data-id="element-911">
            <div className="flex items-center gap-2 flex-wrap" data-id="element-912">
              <h2 className="text-lg font-normal font-heading leading-snug" data-id="element-913">
                {title}
              </h2>
              <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-heading" style={{
              fontVariantNumeric: 'tabular-nums'
            }} data-id="element-914">
                {year}
              </span>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed mt-2" data-id="element-915">
              {definition}
            </p>
            <a href={source} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs text-accent hover:underline mt-3" data-id="element-916">
              <ExternalLink className="h-3 w-3" data-id="element-917" />
              Fonte ufficiale
            </a>
          </div>
        </div>
      </header>

      {note && <div className="px-6 py-3 bg-accent/5 border-b border-border flex items-start gap-2.5" data-id="element-918">
          <Info className="h-4 w-4 text-accent flex-shrink-0 mt-0.5" data-id="element-919" />
          <p className="text-xs text-muted-foreground leading-relaxed" data-id="element-920">
            {note}
          </p>
        </div>}

      {children}
    </section>;
}
export function Definitions() {
  const {
    ihra,
    jda,
    deontologico
  } = antisemitismFrameworks;
  return <div className="px-6 py-6 max-w-[1100px] mx-auto w-full space-y-8" data-id="element-921">
      {/* Header */}
      <header data-id="element-922">
        <div className="flex items-center gap-2 text-sm text-muted-foreground font-heading mb-2" data-id="element-923">
          <BookOpen className="h-4 w-4" data-id="element-924" />
          Riferimenti
        </div>
        <h1 className="text-2xl md:text-3xl font-normal tracking-tight" data-id="element-925">
          Definizioni e quadri di riferimento
        </h1>
        <p className="text-sm text-muted-foreground leading-relaxed mt-3 max-w-3xl" data-id="element-926">
          Il sistema di analisi dell'antisemitismo confronta ciascun articolo
          con tre quadri di riferimento complementari. I criteri qui riportati
          sono parafrasati in italiano a scopo operativo e non sostituiscono i
          testi ufficiali.
        </p>
      </header>

      {/* IHRA */}
      <FrameworkCard icon={Shield} title={ihra.title} year={ihra.year} source={ihra.source} definition={ihra.definition} note={ihra.note} data-id="element-927">
        <ul className="divide-y divide-border" data-id="element-928">
          {ihra.criteria.map(c => <CriterionRow key={c.id} criterion={c} data-id="element-929" />)}
        </ul>
      </FrameworkCard>

      {/* JDA */}
      <FrameworkCard icon={Scale} title={jda.title} year={jda.year} source={jda.source} definition={jda.definition} note={jda.note} data-id="element-930">
        <div className="divide-y divide-border" data-id="element-931">
          {jda.groups.map(group => {
          const isProtected = group.id === 'israel-protected';
          return <div key={group.id} data-id="element-932">
                <div className={`px-6 py-3 flex items-center gap-2 ${isProtected ? 'bg-emerald-50' : 'bg-muted/30'}`} data-id="element-933">
                  {isProtected && <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-shrink-0" data-id="element-934" />}
                  <h3 className={`text-[11px] uppercase tracking-wider font-heading ${isProtected ? 'text-emerald-700' : 'text-muted-foreground'}`} data-id="element-935">
                    {group.title}
                  </h3>
                </div>
                <ul className="divide-y divide-border" data-id="element-936">
                  {group.items.map(item => <CriterionRow key={item.id} criterion={item} protectedItem={isProtected} data-id="element-937" />)}
                </ul>
              </div>;
        })}
        </div>
      </FrameworkCard>

      {/* Codice deontologico */}
      <FrameworkCard icon={BookOpen} title={deontologico.title} year={deontologico.year} source={deontologico.source} definition={deontologico.definition} note={deontologico.note} data-id="element-938">
        <ul className="divide-y divide-border" data-id="element-939">
          {deontologico.criteria.map(c => <CriterionRow key={c.id} criterion={c} data-id="element-940" />)}
        </ul>
      </FrameworkCard>
    </div>;
}